/**
 * Decide whether an external `?redirect=` URL is safe to send the user
 * to after login. Used by the public Login page to honor a redirect
 * param from cross-app entry points (e.g. an unauthenticated invitee
 * opening an installed app's invite link and being bounced through
 * tenant login).
 *
 * Accepted:
 *   - Relative paths starting with `/` (in-app navigation).
 *   - Absolute URLs whose host equals the current host. That covers the
 *     common case of the embedded app and the tenant frontend sharing a
 *     domain (which is the default once INVITE_LINK_BASE_URL is set to
 *     the tenant's friendly root).
 *
 * Rejected (returns null):
 *   - Protocol-relative URLs (`//evil.com/x`) — sneaky open-redirect bait.
 *   - Absolute URLs on a different host — open-redirect bait.
 *   - `javascript:` URLs — XSS bait.
 *   - Anything malformed.
 *
 * Returns the validated URL string when safe, or null otherwise.
 *
 * `currentHost` is taken from `window.location.host` when called in the
 * browser. The parameter override is for unit tests that can't easily
 * stub window.location in jsdom.
 */
export function isSafeReturnUrl(
  raw: string | null,
  currentHost: string = typeof window !== 'undefined' ? window.location.host : '',
): string | null {
  if (!raw) return null;
  if (raw.startsWith('//') || raw.startsWith('\\') || /^javascript:/i.test(raw)) {
    return null;
  }
  if (raw.startsWith('/')) return raw;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (url.host !== currentHost) return null;
    return url.toString();
  } catch {
    return null;
  }
}
